import React, { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ObservationDetailDrawer } from './components/ObservationDetailDrawer';
import { ObservationsCard } from './components/ObservationsCard';
import { useFhirPatientId, usePatientObservations } from './data/observations';
import { findGroupByToken, groupPatientObservations } from './measures';

/**
 * The Observations vertical slice for the new patient profile Overview tab:
 * resolves the FHIR Patient from the route's (Hasura) patient id, loads the
 * observation connection, and owns the card + detail drawer. The open
 * drawer lives in the URL (`?observation=<group token>`) so detail views
 * stay deep-linkable — routing, not component state, is the source of truth.
 */
const DRAWER_PARAM = 'observation';

export const PatientObservationsSection: React.FC = () => {
  const { patientId } = useParams() as { patientId: string };
  const [searchParams, setSearchParams] = useSearchParams();

  const fhirPatient = useFhirPatientId(patientId);
  const observations = usePatientObservations(fhirPatient.data ?? null);

  const grouped = useMemo(
    () => groupPatientObservations(observations.data ?? []),
    [observations.data],
  );

  const openToken = searchParams.get(DRAWER_PARAM);
  const openGroup = findGroupByToken(grouped, openToken);

  const openDrawer = useCallback(
    (token: string) => {
      setSearchParams(
        (params) => {
          params.set(DRAWER_PARAM, token);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closeDrawer = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete(DRAWER_PARAM);
        return params;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  return (
    <>
      <ObservationsCard
        grouped={grouped}
        loading={fhirPatient.loading || observations.loading}
        failed={fhirPatient.failed || observations.failed}
        paging={observations}
        onOpenGroup={openDrawer}
      />
      <ObservationDetailDrawer
        group={openGroup}
        open={Boolean(openToken && openGroup)}
        onClose={closeDrawer}
        onReviewed={observations.refetch}
      />
    </>
  );
};
